import { useCallback, useEffect } from 'react';
import {
  ChakraFlex,
  FAIconName,
  FireflyIconButton,
  FireflyTertiaryButtonMenu,
  MaudSectionHeader,
} from '@equip.health/ui';
import { useHistory, useLocation } from 'react-router-dom';

import { InquiryEvents } from '~/analytics';
import OverviewLoading from '~/components/common/OverviewLoading';
import { PAGE_LAYOUT, PAGE_TOP_PADDING } from '~/lib/constants';
import { InquiryViewOptions } from '~/lib/constants/inquiry.constants';
import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useLocalStorage from '~/lib/hooks/useLocalStorage';
import { isAuthorizedRoute } from '~/lib/util/routes.util';
import { doesCurrentUserHaveRole } from '~/lib/util/user.util';
import { Path } from '~/routes/constants';
import { useAppSelector } from '~/store/hooks';
import {
  useInquiryListDispatch,
  useIsInquiryListLoadingSelector,
} from '~/store/reducers/inquiryList';
import {
  useGetInquiryDetailByIdQuery,
  useGetInquiryViewsQuery,
} from '~/store/services/inquiry';

import InquiryViewsTable from './InquiryViewsTable';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import useErrorToast from '~/lib/hooks/useErrorToast';

const Inquiries = () => {
  const { currentUser, permissions } = useGlobalContext();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const currentInquiryId = queryParams.get('id');
  const { trackEvent } = useAnalytics();
  const isCurrentUserPSR = doesCurrentUserHaveRole(
    currentUser,
    CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE,
  );

  const isAuthorized = isAuthorizedRoute(location.pathname, permissions);

  const { setComponentVisibility } = useComponentPortalContext();
  const isInquiryListLoading = useIsInquiryListLoadingSelector();

  const { reloadInquiryList } = useInquiryListDispatch();
  const numberOfInquiries = useAppSelector(
    (state) => state.inquiryList.numberOfInquiries,
  );
  const { storedValue: activeInquiryView, setValue: setInquiryView } =
    useLocalStorage('inquiryView', InquiryViewOptions.PSR);

  const { data: inquiryViews, isLoading: isInquiryViewsLoading } =
    useGetInquiryViewsQuery();
  const { data: inquiry, isFetching: fetchInquiryLoading } =
    useGetInquiryDetailByIdQuery(currentInquiryId, {
      refetchOnMountOrArgChange: true,
      skip: !currentInquiryId,
    });
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (
      !isInquiryViewsLoading &&
      !activeInquiryView &&
      inquiryViews?.data.length
    ) {
      const defaultView = isCurrentUserPSR
        ? inquiryViews.data.filter((view) => view.code === 'PSR')
        : inquiryViews.data[0];

      setInquiryView(defaultView);
    }
  }, [
    isInquiryViewsLoading,
    inquiryViews,
    activeInquiryView,
    setInquiryView,
    isCurrentUserPSR,
  ]);

  useEffect(() => {
    if (!isAuthorized) {
      history.push(Path.HOME);
    }
  }, [isAuthorized]);

  const handleCloseInquiryDetailDrawer = () => {
    setComponentVisibility({
      component: PortalComponent.INQUIRY_DETAIL_DRAWER,
      data: null,
      isVisible: false,
    });
    trackEvent(InquiryEvents.detailClosed);
    history.push(Path.MAUD_INQUIRIES);
  };

  const openInquiryDetailDrawer = useCallback(() => {
    setComponentVisibility({
      component: PortalComponent.INQUIRY_DETAIL_DRAWER,
      data: {
        inquiryId: currentInquiryId,
        onClose: handleCloseInquiryDetailDrawer,
      },
      isVisible: true,
    });
    trackEvent(InquiryEvents.detailOpened);
  }, [currentInquiryId]);

  /**  
    Get the inquiry id from url, this would usually happen if an inquiry url is directly opened
    e.g. when a user visits /maud/inquiries?id=91359e7e-0025-4c61-a401-75904fecd96a directly by pasting the link to the url bar. 
    This will make sure the inquiry detailed view is opened
  */
  useEffect(() => {
    if (!fetchInquiryLoading && currentInquiryId && inquiry) {
      openInquiryDetailDrawer();
    } else if (!fetchInquiryLoading && currentInquiryId && !inquiry) {
      showErrorToast(
        GlobalErrorCode.INQUIRY_NOT_FOUND,
        404,
        'Sorry we couldn’t find that inquiry.',
      );
    }
  }, [currentInquiryId, fetchInquiryLoading, inquiry, openInquiryDetailDrawer]);

  const handleAddInquiry = () => {
    setComponentVisibility({
      component: PortalComponent.CREATE_INQUIRY_DRAWER,
      isVisible: true,
    });
    trackEvent(InquiryEvents.addInquiryClicked);
  };

  const handleReloadInquiries = () => {
    reloadInquiryList();
    trackEvent(InquiryEvents.reloadInquiriesClicked);
  };

  const handleViewSelect = (selectedOption) => {
    const selectedView = inquiryViews.data.find(
      (view) => view.code === selectedOption,
    );
    if (selectedView) {
      setInquiryView(selectedView);
    }
  };

  return (
    <ChakraFlex
      direction="column"
      gap="24px"
      paddingRight={PAGE_LAYOUT.rightSpacing}
      paddingTop={PAGE_TOP_PADDING}
    >
      <MaudSectionHeader
        count={numberOfInquiries}
        id="inquiries"
        onAdd={handleAddInquiry}
        onAddTitle="Add Inquiry"
        title="Inquiries"
      >
        <FireflyIconButton
          ariaLabel="Reload Inquiries"
          icon={FAIconName.arrowsRotateRegular}
          id="reload-inquiries"
          isLoading={isInquiryListLoading}
          onClick={handleReloadInquiries}
          variant="tertiary"
        />

        <ChakraFlex direction="column" paddingX="12px">
          {inquiryViews?.data && !isInquiryViewsLoading ? (
            <FireflyTertiaryButtonMenu
              buttonId="inquiry-view-select"
              buttonLeftIcon={FAIconName.userGearRegular}
              buttonText={activeInquiryView?.name || 'Select View'}
              onOptionClick={handleViewSelect}
              options={inquiryViews?.data.map((view: InquiryView) => ({
                label: view.name,
                node: view.code,
              }))}
            />
          ) : (
            <OverviewLoading
              data-testId="inquiry-views__loading"
              height={30}
              id="inquiry-views__loading"
              marginTop="0px"
              size="25px"
            />
          )}
        </ChakraFlex>
      </MaudSectionHeader>

      <InquiryViewsTable viewCode={activeInquiryView.code} />
    </ChakraFlex>
  );
};

export default Inquiries;
